// client/src/utils/quotationLinePricingFacts.ts
//
// Which keys of a quotation line's `pricingDetails` describe the PRICE the line
// has and the REFERENCE that price came from — so that a writer putting a new
// price on the line can take out everything that described the old one.
//
// The lists behind BUSINESS_RULES#quotation-line-supplier-price-facts. They lived
// in `quotationLineSupplierPrice.service.js` until 2026-09-14, when a fourth
// writer turned out to exist and not to be on the server: the quotation page's
// "Search Master Catalogue" popup. A pick there replaced the line's name, cost,
// unit and source and carried every one of these keys forward from the row it
// replaced — the old row's `masterCatalogueId`, its pack, and the conversion
// anchor a Unit change restates FROM, so the next Unit change put the previous
// row's price back on the line. The popup cannot call a server service, and a
// second copy of these lists inside the page is the duplication CLAUDE.md's
// golden rule exists to prevent. Hence a mirrored util.
//
// MIRRORED FILE: src/utils/quotationLinePricingFacts.js is a line-for-line twin.
// CRA's ModuleScopePlugin forbids importing from src/, so the copy is the only
// option. Change one, change both — `mirroredUtilParity.test.js` fails if you do
// not.

/** How the cost being replaced was reached. */
export const PRICE_FACTS: readonly string[] = Object.freeze([
  'ttdConversion',
  'supplierPriceRestatement',
  // The price a Unit change restates FROM: left behind, the next unit change
  // puts the previous price back on the line.
  'conversionAnchor',
  // Dropped wherever the anchor is (BUSINESS_RULES#quotation-line-restatable-units).
  'partPackRounding',
  // The marks an earlier pick left: one from the Supplier dropdown, one from
  // the catalogue search popup.
  'supplierDropdownPick',
  'catalogueSearchPick',
  'sourceUom',
]);

/**
 * What the cost being replaced came from: every key a branch of
 * `quotationGeneration.getItemPricing` or `collectVendorOptions` writes about its
 * match. A key added there that describes the match belongs here too, or it
 * outlives the price it described.
 */
export const REFERENCE_FACTS: readonly string[] = Object.freeze([
  'originalItem',
  // The vendor beside `originalItem`'s description — the picked option's own
  // `vendorName|description` identity (`utils/supplierOptionIdentity`), set
  // only by a LISTED-option (Supplier dropdown / merged panel) pick
  // (`quotationLineSupplierPrice.factsForListedOption`, owner audit
  // 2026-09-25). Generation never sets it, so a generation-priced line — which
  // states a description with no vendor to go with it — is read by
  // `itemLookupRows.isPricedRow` as unidentifiable rather than matched by
  // description alone, which used to light up every vendor's identically-
  // worded row.
  'originalItemVendor',
  // D86: the date the picked option's price counts from (ISO or null), written once
  // at pick time by `factsForListedOption`; retires with the price it describes.
  'sourcePriceDate',
  'masterCatalogueId',
  'catalogueSource',
  'fileName',
  'similarity',
  'aiReasoning',
  // D103.2: why the AI could not vet the old match (rate_limited | bad_reply | request_failed).
  'aiFailure',
  'isSubstitute',
  // Which supplier's price the catalogue choice took, and every other supplier's
  // latest (BUSINESS_RULES#catalogue-vendor-row-choice, D25). Left behind, a line
  // somebody re-priced would still name the suppliers the old price was chosen from.
  'priceChoice',
  'doq',
  'allPrices',
  'priceListName',
  'itemCode',
  'packaging',
  'vat',
  'providedUnitPrice',
  'unitConversion',
  'vendorInvoiceMatchHistorical',
  'vendorInvoiceMatchRfq',
  'displayEquivalents',
  // The D63 follow-up popup's own flag (owner's rule, 2026-09-24): the row
  // that priced this line has a BLANK vat_applicable. It describes THAT row,
  // same as masterCatalogueId beside it — left behind, a line re-priced from
  // an answered row (or from a source with no catalogue row at all) would
  // still pop the "Does this item have VAT?" question for a row it no longer
  // prices from.
  'catalogueVatUnset',
  // The VAT percent inside the line's cost, recorded where the cost was set
  // (BUSINESS_RULES#quotation-line-cost-vat, D82): the purchase order takes out
  // exactly this. Left behind, a line re-priced from a VAT-free source would
  // still have VAT taken out of it.
  'costVatPercent',
]);

/**
 * A copy of a line's `pricingDetails` with every price and reference fact taken
 * out. Anything else the details carry is KEPT: a key this file does not name is
 * not known to describe the old price, and dropping it would be a guess.
 *
 * Never mutates its input. A missing or non-object value is an empty object.
 */
export function retiredPricingDetails(details: unknown): Record<string, unknown> {
  const out: Record<string, unknown> =
    details && typeof details === 'object' && !Array.isArray(details)
      ? { ...(details as Record<string, unknown>) }
      : {};
  for (const key of PRICE_FACTS) delete out[key];
  for (const key of REFERENCE_FACTS) delete out[key];
  return out;
}

/**
 * Whether the line's CURRENT price came from a LISTED-option pick (a
 * Supplier-dropdown / merged lookup panel row) — `pricingDetails
 * .supplierDropdownPick`, set unconditionally by `quotationLineSupplierPrice
 * .factsForListedOption` (server) and retired, like every other PRICE_FACT,
 * the moment a later write replaces the price again.
 *
 * The ONE tie-break two independent readers of "which row/kind priced this
 * line" both need (owner audit, 2026-09-25 follow-up): a hand-entered Master
 * Catalogue row is BOTH a catalogue row and a supplier option in the
 * quotation page's merged lookup panel, and picking it as the SUPPLIER row
 * still re-seeds `masterCatalogueId` (so the "Catalogue ↗" link points at the
 * right row) — so `masterCatalogueId` alone cannot tell a catalogue pick from
 * a catalogue-backed supplier pick.
 *   - `itemLookupRows.isPricedRow` (this runtime) uses it to decide which ROW
 *     KIND highlights in the merged panel;
 *   - `quotationLineCatalogueOptions.pricedFromFacts` (server) uses it to
 *     decide the "how was this priced" attribution ('catalogue_row' vs
 *     'supplier_pick') the Catalogue-options link reports — found reading the
 *     stale answer in the same audit, fixed at one owner rather than a
 *     second spelling of the same check.
 */
export function pickedAsSupplierRow(pricingDetails: unknown): boolean {
  return (
    !!pricingDetails &&
    typeof pricingDetails === 'object' &&
    !Array.isArray(pricingDetails) &&
    (pricingDetails as Record<string, unknown>).supplierDropdownPick === true
  );
}
